import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import AuthImage from "@/components/AuthImage";
import { Plus, ArrowRight } from "lucide-react";
import { EXTENSIONS } from "@/lib/extensions";
import { StatusPill } from "@/pages/Profile";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [base, setBase] = useState(null);
  const [boats, setBoats] = useState([]);
  const [exts, setExts] = useState([]);

  useEffect(() => {
    api.get("/profile/base").then((r) => setBase(r.data)).catch(() => {});
    api.get("/profile/extensions").then((r) => setExts(r.data)).catch(() => {});
    api.get("/boats").then((r) => setBoats(r.data)).catch(() => {});
  }, []);

  const pct = base?.completeness ?? 0;
  const showOwner = true;
  const showBoats = true;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
      <p className="eyebrow">Bevela</p>
      <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]" data-testid="dashboard-hello">
        {t.dashboard.hello}, {user?.first_name}.
      </h1>

      <div className="mt-14 grid lg:grid-cols-12 gap-8">
        <section className="lg:col-span-12 card-clean p-8" data-testid="dashboard-extensions-block">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="eyebrow">{t.account.dashExt}</p>
            <Link to="/perfil" className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="dashboard-manage-extensions">{t.account.wantAlso} <ArrowRight className="h-3 w-3" /></Link>
          </div>
          {exts.length === 0 ? (
            <p className="mt-4 text-slate-500 text-sm" data-testid="dashboard-extensions-empty">{t.account.dashExtEmpty}</p>
          ) : (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {exts.map((e) => (
                <Link key={e.type} to={`/perfil/extension/${e.type}`} className="rounded-xl border border-slate-100 bg-[#F8F9FA] p-5 hover:border-slate-300 transition-colors" data-testid={`dashboard-ext-${e.type}`}>
                  <StatusPill status={e.status} lang={lang} />
                  <p className="font-serif text-xl text-[#0B192C] mt-3">{EXTENSIONS[e.type].title[lang]}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
        {showOwner && (
          <section className="lg:col-span-6 card-clean p-8" data-testid="dashboard-profile-block">
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="eyebrow">{t.dashboard.yourProfile}</p>
                <p className="font-serif text-3xl mt-2 text-[#0B192C]">{pct}% <span className="text-slate-400 text-lg align-middle font-body">{t.dashboard.complete}</span></p>
              </div>
              <Link to="/perfil" className="btn-outline text-sm" data-testid="dashboard-complete-profile">
                {t.dashboard.completeProfile}
              </Link>
            </div>
            <div className="mt-6 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-[#C2A878] transition-all" style={{ width: `${pct}%` }} />
            </div>

            <div className="mt-10 p-6 rounded-xl bg-[#F8F9FA] border border-slate-100">
              <p className="eyebrow">{t.dashboard.comingSoon}</p>
              <p className="mt-3 font-serif text-xl text-[#0B192C] leading-snug">{t.dashboard.matchingSoon}</p>
            </div>
          </section>
        )}

        {showBoats && (
          <section className={`${showOwner ? "lg:col-span-6" : "lg:col-span-12"} card-clean p-8`} data-testid="dashboard-boats-block">
            <div className="flex items-center justify-between">
              <div>
                <p className="eyebrow">{t.dashboard.myBoats}</p>
                <p className="font-serif text-3xl mt-2 text-[#0B192C]">{boats.length}</p>
              </div>
              <Link to="/mis-embarcaciones/nueva" className="btn-primary text-sm" data-testid="dashboard-add-boat">
                <Plus className="h-4 w-4" /> {t.boats.add}
              </Link>
            </div>

            <div className="mt-8 space-y-4">
              {boats.length === 0 && (
                <p className="text-slate-500 text-sm">{t.dashboard.noBoats}</p>
              )}
              {boats.slice(0, 3).map((b) => {
                const primary = b.images?.find((i) => i.is_primary) || b.images?.[0];
                return (
                  <Link key={b.id} to={`/embarcacion/${b.id}`} className="flex items-center gap-4 group" data-testid={`dashboard-boat-${b.id}`}>
                    <div className="h-16 w-24 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                      {primary ? <AuthImage path={primary.storage_path} className="w-full h-full object-cover" /> :
                        <div className="w-full h-full bg-slate-100" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-lg text-[#0B192C] truncate">{b.brand} {b.model}</p>
                      <p className="text-xs text-slate-500">{b.year} · {b.length ? `${b.length} m` : ""} · {b.location}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#0B192C] transition-colors" />
                  </Link>
                );
              })}
              <Link to="/mis-embarcaciones" className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="dashboard-see-all-boats">
                {t.boats.title} <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
